import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ShieldCheck, ArrowRight, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function firstName(user) {
  if (!user) return 'there';
  if (user.name) return user.name.split(' ')[0];
  if (user.email) return user.email.split('@')[0];
  return 'there';
}

/**
 * Compact trial-progress card. Shown on the Home hero only to signed-in users.
 * Renders three states:
 *   - active   : trial running, N days left
 *   - expired  : trial ended (trial_started_at set but trial_active=false)
 *   - pending  : signed in, no trial recorded yet → nudge to install the app
 */
export default function TrialProgressCard() {
  const { user, ready } = useAuth();

  const view = useMemo(() => {
    if (!ready || !user) return null;
    if (user.trial_active) {
      const daysLeft = daysUntil(user.trial_expires_at);
      return { kind: 'active', daysLeft, verified: !!user.trial_verified };
    }
    if (user.trial_started_at) return { kind: 'expired' };
    return { kind: 'pending' };
  }, [ready, user]);

  if (!view) return null;

  const commonWrap = "mt-8 max-w-xl rounded-2xl border border-brand-cream/20 bg-brand-cream/10 backdrop-blur px-5 py-4 flex items-center gap-4 animate-fade-in-up-delay-3";
  const name = firstName(user);

  if (view.kind === 'active') {
    return (
      <div data-testid="trial-progress-card" className={commonWrap}>
        <div className="shrink-0 w-11 h-11 rounded-full bg-brand-gold/20 flex items-center justify-center">
          <Clock className="w-5 h-5 text-brand-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-brand-cream font-semibold text-sm md:text-base">
              Trial active — {view.daysLeft} day{view.daysLeft === 1 ? '' : 's'} left
            </span>
            {view.verified ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-gold/20 text-brand-gold text-xs font-medium">
                <ShieldCheck className="w-3 h-3" /> Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-cream/15 text-brand-cream/80 text-xs font-medium">
                Pending verification
              </span>
            )}
          </div>
          <p className="text-brand-cream/70 text-xs md:text-sm mt-0.5">
            Signed in as <span className="text-brand-cream">{user.email}</span>.
            Keep an eye on your inbox for tips as {name} gets started.
          </p>
        </div>
      </div>
    );
  }

  if (view.kind === 'expired') {
    return (
      <div data-testid="trial-progress-card-expired" className={commonWrap}>
        <div className="shrink-0 w-11 h-11 rounded-full bg-brand-cream/15 flex items-center justify-center">
          <AlertCircle className="w-5 h-5 text-brand-cream" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-brand-cream font-semibold text-sm md:text-base">Your trial has ended</p>
          <p className="text-brand-cream/70 text-xs md:text-sm">
            Open the app to continue with a paid plan whenever you&rsquo;re ready.
          </p>
        </div>
      </div>
    );
  }

  // pending: signed-in but no trial recorded yet
  return (
    <Link
      to="/download"
      data-testid="trial-progress-card-pending"
      className={`${commonWrap} hover:bg-brand-cream/15 transition-colors`}
    >
      <div className="shrink-0 w-11 h-11 rounded-full bg-brand-cream/15 flex items-center justify-center">
        <Clock className="w-5 h-5 text-brand-cream" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-brand-cream font-semibold text-sm md:text-base">Welcome back, {name}.</p>
        <p className="text-brand-cream/70 text-xs md:text-sm">
          Install the app to activate your 7-day trial.
        </p>
      </div>
      <ArrowRight className="w-4 h-4 text-brand-cream shrink-0" />
    </Link>
  );
}
